/**
 * Events API
 * 
 * Handles event creation, management, and RSVP functionality.
 */

import { apiGet, apiPost, apiPatch, apiDelete } from '../api';

// Import API_BASE_URL for upload endpoints that use FormData
const API_BASE_URL = import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || '/api';

export interface Event {
  id: string;
  club_id: string;
  club_slug: string | null; // Denormalized for easy club lookup
  title: string;
  description: string | null;
  starts_at: number; // Unix timestamp in seconds
  ends_at: number | null;
  location_name: string | null;
  location_address: string | null;
  location_lat: number | null;
  location_lng: number | null;
  distance_km: number | null; // Optional distance in kilometers
  check_ins_enabled: boolean; // Feature toggle from #206 - enables/disables check-ins for this event
  checkin_radius_m: number | null;
  checkin_window_before_min: number | null;
  checkin_window_after_min: number | null;
  secret_word: string | null;
  secret_word_enabled: boolean;
  is_recurring: number; // 0 or 1 (SQLite boolean)
  hero_image_url: string | null; // Event-specific hero image with fallback to club image
  visibility: 'public' | 'members_only'; // Issue #427: Event visibility control
  created_by: string;
  created_at: number;
  updated_at: number;
}

export interface EventWithRSVP extends Event {
  rsvp_status: 'yes' | 'no' | 'maybe' | null;
  rsvp_count: number;
  // Additional fields for UI
  name?: string; // Alias for title
  club_name?: string; // Denormalized club name
  club_avatar?: string | null; // Denormalized club avatar
  attendee_count?: number; // Alias for rsvp_count
  is_user_attending?: boolean; // Computed from rsvp_status
  // hero_image_url inherited from Event interface
  activity_type?: string | null; // Activity category
  club_visibility?: 'public' | 'private' | 'invite_only'; // Issue #427: Club visibility for validation
}

export interface CreateEventPayload {
  title: string;
  description?: string;
  starts_at: number; // Unix timestamp in seconds
  hero_image_url?: string; // Optional event hero image URL
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  check_ins_enabled?: boolean;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  checkin_window_after_min?: number;
  secret_word?: string;
  secret_word_enabled?: boolean;
  visibility?: 'public' | 'members_only'; // Issue #427: Event visibility control
}

export interface UpdateEventPayload {
  title?: string;
  description?: string;
  starts_at?: number;
  ends_at?: number;
  location_name?: string;
  location_address?: string;
  location_lat?: number;
  location_lng?: number;
  check_ins_enabled?: boolean;
  checkin_radius_m?: number;
  checkin_window_before_min?: number;
  visibility?: 'public' | 'members_only'; // Issue #427: Event visibility control
  checkin_window_after_min?: number;
  secret_word?: string;
  secret_word_enabled?: boolean;
  hero_image_url?: string; // Optional event hero image URL
}

export interface RSVPPayload {
  status: 'yes' | 'no' | 'maybe';
}

export interface EventFilterParams {
  from?: number;
  to?: number;
}

export interface ClubForEventCreation {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  visibility: 'public' | 'private' | 'invite_only';
  my_role: 'owner' | 'leader';
  member_count: number;
}

export interface ClubsForEventCreationResponse {
  clubs: ClubForEventCreation[];
}

export const eventsApi = {
  /**
   * Get clubs where user can create events
   * GET /events/clubs-for-creation
   */
  getClubsForEventCreation: () => 
    apiGet<ClubsForEventCreationResponse>('/events/clubs-for-creation'),

  /**
   * List events (optionally filtered by club)
   * GET /events?club_id={clubId}
   */
  listEvents: (clubId?: string) => {
    const params = clubId ? `?club_id=${clubId}` : '';
    return apiGet<EventWithRSVP[]>(`/events${params}`);
  },

  /**
   * Get events for a specific club with optional date filtering
   * GET /clubs/:clubId/events?from={from}&to={to}
   */
  getClubEvents: (clubId: string, filter?: EventFilterParams) => {
    let url = `/clubs/${clubId}/events`;
    const params = new URLSearchParams();
    
    if (filter?.from) {
      params.append('from', filter.from.toString());
    }
    if (filter?.to) {
      params.append('to', filter.to.toString());
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    return apiGet<EventWithRSVP[]>(url);
  },

  /**
   * Get single event by ID
   * GET /events/:id
   */
  getEvent: (id: string) => apiGet<{ event: EventWithRSVP }>(`/events/${id}`),

  /**
   * Create new event
   * POST /clubs/:clubId/events
   */
  createEvent: (clubId: string, payload: CreateEventPayload) => 
    apiPost<{ event: Event }>(`/clubs/${clubId}/events`, payload),

  /**
   * Update event
   * PATCH /events/:id
   */
  updateEvent: (id: string, payload: UpdateEventPayload) => 
    apiPatch<Event>(`/events/${id}`, payload),

  /**
   * Delete event
   * DELETE /events/:id
   */
  deleteEvent: (id: string) => apiDelete<void>(`/events/${id}`),

  /**
   * RSVP to event
   * POST /events/:id/rsvp
   */
  rsvpToEvent: (id: string, payload: RSVPPayload) => 
    apiPost<void>(`/events/${id}/rsvp`, payload),

  /**
   * Upload event hero image
   * POST /upload/event-hero-image/:eventId
   */
  uploadEventHeroImage: async (eventId: string, file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const response = await fetch(`${API_BASE_URL}/upload/event-hero-image/${eventId}`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Upload failed' }));
      throw new Error(error.message || 'Failed to upload event hero image');
    }
    
    return response.json();
  },

  /**
   * Remove event hero image
   * DELETE /upload/event-hero-image/:eventId
   */
  removeEventHeroImage: async (eventId: string) => {
    const response = await fetch(`${API_BASE_URL}/upload/event-hero-image/${eventId}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Removal failed' }));
      throw new Error(error.message || 'Failed to remove event hero image');
    }
    
    return response.json();
  },

};
